<template>
  <BasicTemplate>
    <h3>
      <router-link to="/metadata-management">Metadata Templates</router-link>
      /
      Template #{{ $route.params.id }}
      <span v-if="editedItem?.name">: {{ editedItem.name }}</span>
    </h3>

    <v-card class="ma-4 pa-4">
      <v-card-text>
        <v-alert v-if="editError" type="error" dense class="mb-3">
          {{ editError }}
        </v-alert>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

        <div v-else>
          <v-card class="ma-2 pa-2">
            <v-card-title><h3>Edit Metadata Template</h3></v-card-title>
            <v-card-text>
              <v-text-field v-if="'name' in editedItem" v-model="editedItem.name" label="Name" />
              <v-text-field v-if="'slug' in editedItem" v-model="editedItem.slug" label="Slug" />
              <v-textarea v-if="'description' in editedItem" v-model="editedItem.description" label="Description" rows="3" />
              <v-combobox
                v-model="selectedSuggestedGames"
                :items="allGames"
                item-text="slug"
                return-object
                chips
                multiple
                label="Suggested Games"
                data-testid="meta-template-suggested-games"
              />
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" :loading="templateSaving" @click="saveTemplate">Save</v-btn>
              <v-spacer />
              <v-btn
                color="warning"
                text
                :disabled="templateSaving"
                @click="deleteTemplate"
              >
                Delete Template
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card class="ma-2 pa-2">
            <v-card-title><h3>This template can be applicable to Games</h3></v-card-title>
            <v-card-text>
              <div class="d-flex align-center">
                <v-autocomplete
                  v-model="selectedSupportedGame"
                  :items="allGames"
                  :item-text="gameLabel"
                  return-object
                  label="Add game"
                  class="flex-grow-1 mr-3"
                  clearable
                  :search-input.sync="supportedGameSearch"
                />
                <v-btn color="primary" @click="addSupportedGame" :disabled="!selectedSupportedGame">
                  Add
                </v-btn>
              </div>
              <v-data-table
                :headers="supportedGameHeaders"
                :items="supportedGames"
                dense
                hide-default-footer
              >
                <template v-slot:item.actions="{ item }">
                  <v-icon color="warning" @click="removeSupportedGame(item)">mdi-delete</v-icon>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <v-card class="ma-2 pa-2">
            <v-card-title><h3>This template assigned to Games</h3></v-card-title>
            <v-card-text>
              <div class="d-flex align-center">
                <v-autocomplete
                  v-model="selectedAssignedGame"
                  :items="supportedGames"
                  :item-text="gameLabel"
                  return-object
                  label="Add game"
                  class="flex-grow-1 mr-3"
                  clearable
                  :search-input.sync="assignedGameSearch"
                />
                <v-btn color="primary" @click="addAssignedGame" :disabled="!selectedAssignedGame">
                  Add
                </v-btn>
              </div>
              <v-data-table
                :headers="editableGamesHeaders"
                :items="gamesList"
                dense
                hide-default-footer
              >
                <template v-slot:item.actions="{ item }">
                  <v-icon color="warning" @click="removeAssignedGame(item)">mdi-delete</v-icon>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <v-card class="ma-2 pa-2">
            <v-card-title><h3>This template assigned to Feed Games</h3></v-card-title>
            <v-card-text>
              <div class="d-flex align-center mb-3">
                <v-select
                  v-model="selectedFeedForFeedGame"
                  :items="allFeeds"
                  item-text="name"
                  item-value="id"
                  label="Select Feed"
                  class="flex-grow-1 mr-3"
                  clearable
                  @change="loadFeedGames"
                />
              </div>
              <div class="d-flex align-center">
                <v-autocomplete
                  v-model="selectedAssignedFeedGame"
                  :items="availableGamesForSelectedFeed"
                  :item-text="gameLabel"
                  return-object
                  label="Add game"
                  class="flex-grow-1 mr-3"
                  clearable
                  :search-input.sync="assignedFeedGameSearch"
                  :disabled="!selectedFeedForFeedGame"
                  no-data-text="No applicable games found"
                />
                <v-btn color="primary" @click="addAssignedFeedGame" :disabled="!selectedAssignedFeedGame || !selectedFeedForFeedGame">
                  Add
                </v-btn>
              </div>
              <v-data-table
                :headers="feedGamesHeaders"
                :items="feedGamesList"
                dense
                hide-default-footer
                no-data-text="No applicable games found"
              >
                <template v-slot:item.actions="{ item }">
                  <v-icon color="warning" @click="removeAssignedFeedGame(item)">mdi-delete</v-icon>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <v-card class="ma-2 pa-2">
            <v-card-title><h3>Locales</h3></v-card-title>
            <v-card-text>
              <v-tabs>
                <v-tab
                  v-for="locale in gameLocales"
                  :key="locale.value"
                  :href="`#meta-tab-${locale.value}`"
                  :data-testid="`meta-template-locale-tab-${locale.value}`"
                >
                  {{ locale.name }}
                </v-tab>
                <v-tab-item
                  v-for="meta in editedLocales"
                  :key="meta.locale"
                  :value="'meta-tab-' + meta.locale"
                  :data-testid="`meta-template-locale-panel-${meta.locale}`"
                >
                  <v-card class="pa-4 ma-4">
                    <v-form v-if="meta">
                      <v-text-field label="Release Date" type="date" v-model="meta.gameReleaseDate" />
                      <v-text-field label="Background" type="color" v-model="meta.backgroundColor" />

                      <v-text-field label="Alias" v-model="meta.alias"></v-text-field>
                      <v-text-field label="Name" v-model="meta.name"></v-text-field>
                      <v-text-field label="Tile Rollover" v-model="meta.details"></v-text-field>
                      <v-text-field label="Full Title" v-model="meta.fullTitle"></v-text-field>

                      <v-text-field label="Seo Title" v-model="meta.htmlTitle" />
                      <v-text-field label="Seo Title (Ark.com)" v-model="meta.seoTitle" />
                      <v-textarea label="Seo Description" v-model="meta.htmlDescription" rows="3" />

                      <v-textarea label="Description" v-model="meta.description" rows="4" />
                      <v-textarea label="Rules" v-model="meta.rules" rows="5" />

                      <v-combobox
                        label="Primary Category"
                        v-model="meta.primaryCategory"
                        :items="categories.filter(c => c.locale === meta.locale)"
                        item-text="name"
                        return-object
                      />
                      <v-combobox
                        label="Categories"
                        v-model="meta.categories"
                        :items="categories.filter(c => c.locale === meta.locale)"
                        item-text="name"
                        return-object
                        chips
                        multiple
                      />
                      <v-combobox
                        label="Tags"
                        v-model="meta.tags"
                        :items="tags.filter(c => c.locale === meta.locale)"
                        item-text="name"
                        return-object
                        chips
                        multiple
                      />
                      <v-combobox
                        label="Badges"
                        v-model="meta.badges"
                        :items="badges.filter(c => c.locale === meta.locale)"
                        item-text="name"
                        return-object
                        chips
                        multiple
                      />
                    </v-form>
                    <v-card-actions>
                      <v-btn
                        color="primary"
                        :loading="localeSavingId === meta.id && !isNewLocale(meta)"
                        @click="saveLocale(meta)"
                      >
                        Save Locale
                      </v-btn>
                      <v-spacer />
                      <v-btn
                        color="warning"
                        text
                        :disabled="localeSavingId === meta.id && !isNewLocale(meta)"
                        @click="deleteLocale(meta)"
                      >
                        Delete Locale
                      </v-btn>
                    </v-card-actions>
                  </v-card>

                  <v-card class="ma-4 pa-4">
                    <v-card-title><h3>Thumbs</h3></v-card-title>
                    <v-card-text>
                      <MetaTemplateThumbs
                        v-if="meta.id"
                        :metaTemplateId="editedItem?.id || $route.params.id"
                        :metaId="meta.id"
                        :locale="meta.locale"
                      />
                      <h2 class="ml-4 red--text" v-else>
                        (Uploading thumbnails is available after creating and saving metadata for the locale)
                      </h2>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <v-tab-item
                  v-for="locale in missingLocales"
                  :key="locale.value"
                  :value="'meta-tab-' + locale.value"
                  :data-testid="`meta-template-locale-panel-${locale.value}`"
                >
                  <v-form>
                    <p class="text-center">Nothing here yet.</p>
                      <v-row justify="center">
                        <v-btn class="ma-4" color="primary" @click="createLocale(locale.value)">Add</v-btn>
                    </v-row>
                  </v-form>
                </v-tab-item>
              </v-tabs>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import { gameLocales } from '@/constants/locales';
import PrismEditor from 'vue-prism-editor';
import MetaTemplateThumbs from '../../components/MetaTemplateThumbs/MetaTemplateThumbs.vue';

export default {
  name: 'MetadataManagementEdit',
  components: { BasicTemplate, PrismEditor, MetaTemplateThumbs },
  data() {
    return {
      loading: false,
      editError: '',
      editedItem: {},
      editedLocales: [],
      categories: [],
      tags: [],
      badges: [],
      gameLocales,
      localeSavingId: null,
      newLocaleSavingKey: null,
      templateSaving: false,
      gamesHeaders: [
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
      ],
      editableGamesHeaders: [
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      feedGamesHeaders: [
        { text: 'Feed', value: 'feedName' },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      supportedGameHeaders: [
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      supportedGames: [],
      assignedGames: [],
      assignedFeedGames: [],
      allGames: [],
      allFeeds: [],
      selectedSupportedGame: null,
      selectedAssignedGame: null,
      selectedAssignedFeedGame: null,
      selectedFeedForFeedGame: null,
      feedGames: [],
      supportedGameSearch: '',
      assignedGameSearch: '',
      assignedFeedGameSearch: '',
      selectedSuggestedGames: [],
    };
  },
  computed: {
    missingLocales() {
      return this.gameLocales.filter(locale => !this.editedLocales?.some(meta => meta.locale === locale.value));
    },
    gamesList() {
      return this.assignedGames;
    },
    feedGamesList() {
      if (!Array.isArray(this.assignedFeedGames)) return [];
      const gamesById = new Map(this.allGames.map(game => [String(game.id), game]));
      const feedsById = new Map(this.allFeeds.map(feed => [String(feed.id), feed]));
      return this.assignedFeedGames.map(item => {
        const gameId = item?.gameId ?? item?.id;
        const feedId = item?.feedId;
        const game = gamesById.get(String(gameId));
        const feed = feedsById.get(String(feedId));
        return {
          ...item,
          gameId,
          feedId,
          slug: game?.slug ?? item?.slug ?? '',
          name: game?.name ?? item?.name ?? '',
          feedName: feed?.name ?? '',
        };
      });
    },
    availableGamesForSelectedFeed() {
      if (!this.selectedFeedForFeedGame || !this.feedGames.length) {
        return [];
      }
      // Get game IDs from the selected feed
      const feedGameIds = new Set(
        this.feedGames
          .map(fg => fg?.gameId ?? fg?.game?.id ?? fg?.id)
          .filter(Boolean)
          .map(id => String(id))
      );
      // Filter supportedGames to only include games that are in the selected feed
      return this.supportedGames.filter(game => 
        feedGameIds.has(String(game.id))
      );
    },
  },
  async created() {
    await Promise.all([
      this.load(),
      this.loadDictionaries(),
      this.loadGames(),
      this.loadFeeds(),
    ]);
  },
  methods: {
    async loadDictionaries() {
      try {
        const [categories, tags, badges] = await Promise.all([
          this.$gameApi.get('/dictionary/categories'),
          this.$gameApi.get('/dictionary/tags'),
          this.$gameApi.get('/dictionary/badges'),
        ]);
        this.categories = categories || [];
        this.tags = tags || [];
        this.badges = badges || [];
      } catch (error) {
        console.error('Error loading dictionaries:', error);
      }
    },
    async load() {
      this.loading = true;
      this.editError = '';
      try {
        await this.$msal.ensureAuthenticated();
        const { id } = this.$route.params;
        const [template, metas] = await Promise.all([
          this.$gameApi.get(`/meta-templates/${id}`),
          this.$gameApi.get(`/metaTemplates/${id}/metas`),
        ]);
        this.editedItem = template || {};
        this.normalizeLocales(metas);
        this.syncSupportedGames();
        this.syncAssignedGames();
        this.syncSuggestedGames();
      } catch (error) {
        console.error('Error loading meta template:', error);
        this.editError = error?.message || 'Failed to load template.';
      } finally {
        this.loading = false;
      }
    },
    async loadGames() {
      try {
        await this.$msal.ensureAuthenticated();
        const response = await this.$gameApi.get('/games');
        const games = Array.isArray(response) ? response : (response?.items || response?.data || []);
        this.allGames = this.normalizeGames(games);
        this.syncSupportedGames();
        this.syncAssignedGames();
        this.syncSuggestedGames();
      } catch (error) {
        console.error('Error loading games:', error);
        this.allGames = [];
      }
    },
    async loadFeeds() {
      try {
        await this.$msal.ensureAuthenticated();
        const response = await this.$gameApi.get('/feeds');
        const feeds = Array.isArray(response) ? response : (response?.items || response?.data || []);
        this.allFeeds = feeds || [];
        this.syncAssignedGames();
      } catch (error) {
        console.error('Error loading feeds:', error);
        this.allFeeds = [];
      }
    },
    async loadFeedGames() {
      if (!this.selectedFeedForFeedGame) {
        this.feedGames = [];
        return;
      }
      try {
        await this.$msal.ensureAuthenticated();
        const feedResponse = await this.$gameApi.get(`/feeds/${this.selectedFeedForFeedGame}`);
        this.feedGames = feedResponse?.feedGames || [];
      } catch (error) {
        console.error('Error loading feed games:', error);
        this.feedGames = [];
      }
    },
    normalizeGame(game) {
      if (!game) return null;
      const nestedGame = game.game || game.feedGame?.game || null;
      const source = nestedGame || game.feedGame || game;
      const id = source.gameId ?? source.id ?? game.gameId ?? game.id;
      const slug = source.slug ?? nestedGame?.slug ?? game.slug ?? '';
      const name = source.name ?? nestedGame?.name ?? game.name ?? '';
      if (!id && !slug && !name) return null;
      return { id, slug, name };
    },
    normalizeFeedGame(item) {
      if (!item) return null;
      const feedId = item.feedId ?? item.feed?.id ?? item.feedGame?.feedId;
      const gameId = item.gameId ?? item.game?.id ?? item.feedGame?.gameId ?? item.id;
      const feedGameId = item.id ?? item.feedGameId;
      const gameSource = item.game || item.feedGame?.game || item;
      const slug = gameSource?.slug ?? item.slug ?? '';
      const name = gameSource?.name ?? item.name ?? '';
      if (!gameId && !slug && !name) return null;
      return { id: gameId, gameId, feedId, feedGameId, slug, name };
    },
    normalizeFeedGames(list) {
      if (!Array.isArray(list)) return [];
      const normalized = list
        .map(item => this.normalizeFeedGame(item))
        .filter(Boolean);
      const seen = new Set();
      return normalized.filter(game => {
        const key = `${String(game.feedId ?? '')}:${String(game.gameId ?? game.id ?? '')}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    normalizeGames(games) {
      if (!Array.isArray(games)) return [];
      const normalized = games
        .map(game => this.normalizeGame(game))
        .filter(Boolean);
      const seen = new Set();
      return normalized.filter(game => {
        const key = String(game.id ?? game.slug ?? '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    syncSupportedGames() {
      if (!this.editedItem) return;
      if (Array.isArray(this.editedItem.supportedGames) && this.editedItem.supportedGames.length) {
        this.supportedGames = this.normalizeGames(this.editedItem.supportedGames);
        return;
      }

      const ids = Array.isArray(this.editedItem.supportedGameIds)
        ? this.editedItem.supportedGameIds
        : [];
      if (ids.length && this.allGames.length) {
        const idSet = new Set(ids.map(id => String(id)));
        this.supportedGames = this.allGames
          .filter(game => idSet.has(String(game.id)))
          .map(game => this.normalizeGame(game))
          .filter(Boolean);
        return;
      }

      this.supportedGames = [];
    },
    filterToSupportedGames(list) {
      if (!Array.isArray(list)) return [];
      if (!this.supportedGames?.length) return list;
      const idSet = new Set(this.supportedGames.map(game => String(game.id)));
      return list.filter(game => idSet.has(String(game.id)));
    },
    syncAssignedGames() {
      if (!this.editedItem) return;
      const games = this.normalizeGames(this.editedItem.games);
      const feedGames = this.normalizeFeedGames(this.editedItem.feedGames);
      this.assignedGames = this.filterToSupportedGames(games);
      this.assignedFeedGames = this.filterToSupportedGames(feedGames);
    },
    syncSuggestedGames() {
      if (!this.editedItem) return;
      this.selectedSuggestedGames = this.normalizeGames(this.editedItem.suggestedGames || []);
    },
    gameLabel(game) {
      if (!game) return '';
      if (game.name && game.slug) {
        return `${game.name} (${game.slug})`;
      }
      if (game.name) return game.name;
      if (game.slug) return game.slug;
      return '';
    },
    async updateSupportedGames() {
      const templateId = this.editedItem?.id || this.$route.params.id;
      await this.$msal.ensureAuthenticated();
      await this.$gameApi.patch(`/meta-templates/${templateId}`, {
        supportedGames: this.supportedGames.map(game => ({ id: game.id })),
      });
    },
    async updateAssignedGames() {
      const templateId = this.editedItem?.id || this.$route.params.id;
      await this.$msal.ensureAuthenticated();
      await this.$gameApi.patch(`/meta-templates/${templateId}`, {
        games: this.assignedGames.map(game => ({ id: game.id })),
      });
    },
    async updateAssignedFeedGames() {
      const templateId = this.editedItem?.id || this.$route.params.id;
      await this.$msal.ensureAuthenticated();
      await this.$gameApi.patch(`/meta-templates/${templateId}`, {
        feedGames: this.assignedFeedGames.map(game => ({ id: game.feedGameId })),
      });
    },
    async addSupportedGame() {
      if (!this.selectedSupportedGame) return;
      const normalized = this.normalizeGame(this.selectedSupportedGame);
      if (!normalized?.id) {
        this.editError = 'Selected game is invalid. Please choose another game.';
        return;
      }
      const exists = this.supportedGames.some(game => String(game.id) === String(normalized.id));
      if (exists) {
        this.selectedSupportedGame = null;
        return;
      }
      this.supportedGames = [...this.supportedGames, normalized];
      this.selectedSupportedGame = null;
      try {
        await this.updateSupportedGames();
        this.$swal.fire({
          icon: 'success',
          title: 'Game added.',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (error) {
        this.editError = this.describeError(error);
      }
    },
    async addAssignedGame() {
      if (!this.selectedAssignedGame) return;
      const normalized = this.normalizeGame(this.selectedAssignedGame);
      if (!normalized?.id) {
        this.editError = 'Selected game is invalid. Please choose another game.';
        return;
      }
      const exists = this.assignedGames.some(game => String(game.id) === String(normalized.id));
      if (exists) {
        this.selectedAssignedGame = null;
        return;
      }
      this.assignedGames = [...this.assignedGames, normalized];
      this.selectedAssignedGame = null;
      try {
        await this.updateAssignedGames();
        this.$swal.fire({
          icon: 'success',
          title: 'Game added.',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (error) {
        this.editError = this.describeError(error);
      }
    },
    async addAssignedFeedGame() {
      if (!this.selectedAssignedFeedGame || !this.selectedFeedForFeedGame) return;
      const normalized = this.normalizeGame(this.selectedAssignedFeedGame);
      if (!normalized?.id) {
        this.editError = 'Selected game is invalid. Please choose another game.';
        return;
      }
      const feedId = this.selectedFeedForFeedGame;
      // Find the feedGame object from feedGames that matches the selected game
      const feedGameObj = this.feedGames.find(
        fg => String(fg?.gameId ?? fg?.game?.id ?? fg?.id) === String(normalized.id)
      );
      if (!feedGameObj?.id) {
        this.editError = 'Feed game not found. Please select a valid game from the feed.';
        return;
      }
      const exists = this.assignedFeedGames.some(
        game => String(game.feedGameId) === String(feedGameObj.id)
      );
      if (exists) {
        this.selectedAssignedFeedGame = null;
        return;
      }
      const feedGame = {
        ...normalized,
        gameId: normalized.id,
        feedId: feedId,
        feedGameId: feedGameObj.id,
      };
      this.assignedFeedGames = [...this.assignedFeedGames, feedGame];
      this.selectedAssignedFeedGame = null;
      this.selectedFeedForFeedGame = null;
      try {
        await this.updateAssignedFeedGames();
        this.$swal.fire({
          icon: 'success',
          title: 'Game added.',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (error) {
        this.editError = this.describeError(error);
      }
    },
    async removeSupportedGame(game) {
      this.supportedGames = this.supportedGames.filter(item => item.id !== game.id);
      try {
        await this.updateSupportedGames();
        const nextAssignedGames = this.assignedGames.filter(item => item.id !== game.id);
        const nextAssignedFeedGames = this.assignedFeedGames.filter(item => item.id !== game.id);
        const assignmentsChanged = nextAssignedGames.length !== this.assignedGames.length
          || nextAssignedFeedGames.length !== this.assignedFeedGames.length;
        if (assignmentsChanged) {
          this.assignedGames = nextAssignedGames;
          this.assignedFeedGames = nextAssignedFeedGames;
          await Promise.all([
            this.updateAssignedGames(),
            this.updateAssignedFeedGames(),
          ]);
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Game removed.',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (error) {
        this.editError = this.describeError(error);
      }
    },
    async removeAssignedGame(game) {
      this.assignedGames = this.assignedGames.filter(item => item.id !== game.id);
      try {
        await this.updateAssignedGames();
        this.$swal.fire({
          icon: 'success',
          title: 'Game removed.',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (error) {
        this.editError = this.describeError(error);
      }
    },
    async removeAssignedFeedGame(game) {
      this.assignedFeedGames = this.assignedFeedGames.filter(item => item.feedGameId !== game.feedGameId);
      try {
        await this.updateAssignedFeedGames();
        this.$swal.fire({
          icon: 'success',
          title: 'Game removed.',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (error) {
        this.editError = this.describeError(error);
      }
    },
    normalizeLocales(metas) {
      const locales = Array.isArray(metas) ? metas : [];
      this.editedLocales = JSON.parse(JSON.stringify(locales));
      this.editedLocales.forEach(meta => {
        if (meta.backgroundColor) meta.backgroundColor = meta.backgroundColor.substr(0, 7);
        // Normalize \n to empty string for text fields when loading data
        const textFields = ['description', 'rules', 'htmlDescription', 'htmlTitle'];
        textFields.forEach(field => {
          if (meta[field] === '\n') {
            meta[field] = '';
          }
        });
      });
    },
    normalizeItems(items) {
      if (!Array.isArray(items)) return [];
      return items.map(item => ({
        slug: item?.slug ?? item?.game?.slug ?? item?.feedGame?.slug ?? '',
        name: item?.name ?? item?.game?.name ?? item?.feedGame?.name ?? '',
      })).filter(item => item.slug || item.name);
    },
    createLocale(locale) {
      const englishAlias = this.editedLocales
        ?.find(meta => meta.locale === 'en' && meta.alias && meta.alias.trim())
        ?.alias.trim();

      this.editedLocales.push({
        locale,
        alias: locale !== 'en' && englishAlias ? englishAlias : '',
        name: '',
        details: '',
        fullTitle: '',
        seoTitle: '',
        subTitle: '',
        htmlTitle: '',
        htmlDescription: '',
        description: '',
        rules: '',
        categories: [],
        primaryCategory: null,
        tags: [],
        badges: []
      });
    },
    transformation({ locale }) {
      return (name) => typeof name === 'string' ? { name, locale } : name;
    },
    async saveLocale(meta) {
      this.editError = '';
      const templateId = this.editedItem?.id || this.$route.params.id;
      if (!templateId) {
        this.editError = 'Template id is missing. Unable to save changes.';
        return;
      }

      this.localeSavingId = meta.id || null;
      this.newLocaleSavingKey = meta.id ? null : meta.locale;
      try {
        const transform = this.transformation(meta);
        meta.primaryCategory = transform(meta.primaryCategory);
        meta.categories = meta.categories?.map(transform) || [];
        meta.tags = meta.tags?.map(transform) || [];
        meta.badges = meta.badges?.map(transform) || [];
        meta.gameReleaseDate = meta.gameReleaseDate === '' ? null : meta.gameReleaseDate;

        const payload = this.buildMetaPayload(meta, templateId);

        await this.$msal.ensureAuthenticated();
        if (meta.id) {
          await this.$gameApi.patch(`/metaTemplates/${templateId}/metas/${meta.id}`, payload);
        } else {
          const created = await this.$gameApi.post(`/metaTemplates/${templateId}/metas`, payload);
          if (created?.id) {
            this.$set(meta, 'id', created.id);
          }
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Metadata template saved.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error saving meta template:', error);
        this.editError = this.describeError(error);
      } finally {
        this.localeSavingId = null;
        this.newLocaleSavingKey = null;
      }
    },
    async saveTemplate() {
      this.editError = '';
      const templateId = this.editedItem?.id || this.$route.params.id;
      if (!templateId) {
        this.editError = 'Template id is missing. Unable to save changes.';
        return;
      }

      this.templateSaving = true;
      try {
        const payload = this.buildTemplatePayload(templateId);
        await this.$msal.ensureAuthenticated();
        const updated = await this.$gameApi.patch(`/meta-templates/${templateId}`, payload);
        this.editedItem = { ...this.editedItem, ...updated };
        this.syncSuggestedGames();
        this.$swal.fire({
          icon: 'success',
          title: 'Metadata template saved.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error saving meta template:', error);
        this.editError = this.describeError(error);
      } finally {
        this.templateSaving = false;
      }
    },
    async deleteTemplate() {
      const templateId = this.editedItem?.id || this.$route.params.id;
      if (!templateId) {
        this.editError = 'Template id is missing. Unable to delete.';
        return;
      }

      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete template?',
        text: 'This will remove the template and all its locales.',
        showCancelButton: true,
      });

      if (!isConfirmed) return;

      this.templateSaving = true;
      try {
        await this.$msal.ensureAuthenticated();
        const status = await this.$gameApi.delete(`/meta-templates/${templateId}`);
        if (status === 400) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Cannot delete template',
            text: 'Template has references. You should manage them first, then delete it.',
            timer: 4000,
          });
          return;
        }
        // Accept both 200 and 204 as success statuses for DELETE
        if (status !== 200 && status !== 204 && (status < 200 || status >= 300)) {
          throw new Error(`Failed to delete template: status ${status}`);
        }
        await this.$swal.fire({
          icon: 'success',
          title: 'Template deleted.',
          timer: 1500,
          showConfirmButton: false,
        });
        await this.$router.push('/metadata-management');
      } catch (error) {
        console.error('Error deleting meta template:', error);
        await this.$swal.fire({
          icon: 'error',
          title: 'Failed to delete template',
          text: error?.message || this.describeError(error),
          timer: 3000,
        });
        this.editError = this.describeError(error);
      } finally {
        this.templateSaving = false;
      }
    },
    buildTemplatePayload(templateId) {
      const {
        games,
        feedGames,
        createdAt,
        updatedAt,
        metas,
        supportedGames,
        supportedGameIds,
        suggestedGames,
        ...rest
      } = this.editedItem || {};

      return {
        ...rest,
        id: templateId,
        supportedGames: this.supportedGames.map(game => ({ id: game.id })),
        suggestedGames: this.selectedSuggestedGames.map(game => ({ id: game.id })),
      };
    },
    buildMetaPayload(meta, templateId) {
      const {
        id,
        createdAt,
        updatedAt,
        game,
        feedGame,
        ...rest
      } = meta || {};

      const payload = {
        ...rest,
        id: id || undefined,
      };

      if (!id) {
        payload.metaTemplateId = templateId;
      }

      return payload;
    },
    isNewLocale(meta) {
      return !meta?.id && this.newLocaleSavingKey === meta?.locale;
    },
    async deleteLocale(meta) {
      const templateId = this.editedItem?.id || this.$route.params.id;
      if (!meta) return;

      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete locale?',
        text: 'This will remove locale metadata.',
        showCancelButton: true,
      });

      if (!isConfirmed) return;

      if (!meta.id) {
        this.editedLocales = this.editedLocales.filter(item => item !== meta);
        return;
      }

      this.localeSavingId = meta.id;
      this.newLocaleSavingKey = null;
      try {
        await this.$msal.ensureAuthenticated();
        await this.$gameApi.delete(`/metaTemplates/${templateId}/metas/${meta.id}`);
        this.editedLocales = this.editedLocales.filter(item => item.id !== meta.id);
        this.$swal.fire({
          icon: 'success',
          title: 'Locale deleted.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error deleting meta locale:', error);
        this.editError = this.describeError(error);
      } finally {
        this.localeSavingId = null;
      }
    },
    describeError(error) {
      if (!error) return 'Failed to save template.';
      if (typeof error === 'string') return error;
      if (error.message) return error.message;
      try {
        return JSON.stringify(error);
      } catch (e) {
        return 'Failed to save template.';
      }
    },
  },
};
</script>
